import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FileText, Shield, Users, Globe, AlertTriangle, Scale, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

export default function Terms() {
  return (
    <Layout>
      <Helmet>
        <title>Terms of Service — Transparency Badge Website Safety Checker</title>
        <meta name="description" content="Terms of Service for the free website safety checker and Verified Badge Program. Rules for acceptable use, liability, and subscription terms." />
        <link rel="canonical" href="https://transparencybadge.com/terms" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Legal"
            title="Terms of Service"
            description="Please read these terms carefully before using the Universal Website Transparency Badge service."
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          {/* Introduction */}
          <div className="bg-card rounded-2xl border p-8 mb-10">
            <p className="text-muted-foreground">
              Welcome to the Universal Website Transparency Badge. These Terms of Service ("Terms") govern your access to and use of our website and all related services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
            </p>
          </div>

          <div className="space-y-10">
            {/* 1. Acceptance */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                1. Acceptance of Terms
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  By accessing, browsing, or using the Universal Website Transparency Badge website, tools, or any associated services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                </p>
                <p className="text-muted-foreground">
                  If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms. In such case, "you" and "your" will refer to that organization.
                </p>
                <p className="text-muted-foreground">
                  We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Service following any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
                </p>
              </div>
            </div>

            {/* 2. Description of Service */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                2. Description of Service
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  The Universal Website Transparency Badge is a public safety transparency project that evaluates website trustworthiness using publicly available data sources and safety signals. The Service provides:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "A free website safety checker — evaluate any domain using public threat intelligence, SSL checks, domain age, and more",
                    "Color-coded results (Established, New/Unknown, Risk, Verified by Transparency Badge) reflecting aggregated safety signals",
                    "Information about domain age, SSL certificates, server location, and threat intelligence",
                    "Educational content about online safety and website verification",
                    "A Verified Badge Program for website owners — a paid subscription ($10/month per domain) that includes a one-time eligibility check, continuous automated monitoring, and an embeddable trust badge",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  The website checker is provided free of charge. The Verified Badge Program is a paid subscription service. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without prior notice.
                </p>
              </div>
            </div>

            {/* 3. Verified Badge Program */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                3. Verified Badge Program
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  The Verified Badge Program is a paid subscription service available to website owners. By applying for and subscribing to the Verified Badge Program, you agree to the following additional terms:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "The subscription fee is $10/month per domain. The first month is free. Billing begins from the second month.",
                    "You must provide accurate information in your application. Providing false or misleading information will result in immediate badge revocation without refund.",
                    "We reserve the right to approve or reject any application at our sole discretion. If rejected, you will not be charged.",
                    "Your badge will be revoked immediately if your website is found to violate our safety standards at any time, with no refund for the current billing period.",
                    "You may cancel your subscription at any time. Your badge will remain active until the end of the current billing period.",
                    "We store your application data (name, email, domain, description) to manage your subscription. See our Privacy Policy for details.",
                    "The Verified Badge embed code may only be used on the specific domain approved in your application.",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 4. Use of Service */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                4. Acceptable Use
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Use the Service for any illegal, harmful, or fraudulent purpose",
                    "Attempt to gain unauthorized access to our systems, servers, or databases",
                    "Use automated tools, bots, or scripts to access the Service in a way that exceeds reasonable use or impacts service availability for other users",
                    "Misrepresent the results of our transparency reports or badge assessments",
                    "Use our badge assessments to defame, harass, or harm other website owners without factual basis",
                    "Reverse engineer, decompile, or disassemble any part of the Service",
                    "Remove, alter, or obscure any copyright, trademark, or other proprietary notices",
                    "Use the Service to create a competing product or service",
                    "Interfere with or disrupt the integrity or performance of the Service",
                    "Transmit any viruses, malware, or other harmful code through the Service",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  We reserve the right to restrict or terminate access to the Service for any user who violates these acceptable use guidelines.
                </p>
              </div>
            </div>

            {/* 5. Informational Purpose */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-primary" />
                5. Informational Purpose & No Warranties
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  The Service is provided for informational and educational purposes only. All transparency reports, badge assessments, and safety evaluations are based on publicly available data at the time of analysis and are provided "AS IS" and "AS AVAILABLE" without any warranties of any kind, whether express, implied, or statutory.
                </p>
                <p className="text-muted-foreground">
                  We specifically disclaim all warranties, including but not limited to:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Warranties of merchantability, fitness for a particular purpose, and non-infringement",
                    "Warranties regarding the accuracy, reliability, completeness, or timeliness of any information provided",
                    "Warranties that the Service will be uninterrupted, error-free, or secure",
                    "Warranties that the results obtained from the Service will be accurate or reliable",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  This check is based on public technical safety signals available at the time of analysis. It does not verify ownership, official status, or business practices. Always do your own research.
                </p>
              </div>
            </div>

            {/* 6. Limitation of Liability */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Scale className="h-5 w-5 text-primary" />
                6. Limitation of Liability
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  To the maximum extent permitted by applicable law, the Universal Website Transparency Badge, its operators, contributors, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from or related to:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Your use of or inability to use the Service",
                    "Any errors, inaccuracies, or omissions in our transparency reports or badge assessments",
                    "Any unauthorized access to or alteration of your data or transmissions",
                    "Any decisions you make based on information provided by the Service",
                    "Any interactions you have with third-party websites evaluated through our Service",
                    "Any loss of profits, data, goodwill, or other intangible losses",
                    "Any service interruptions, downtime, or unavailability",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  This limitation applies regardless of the legal theory on which the claim is based, including negligence, breach of contract, or any other legal theory, even if we have been advised of the possibility of such damages.
                </p>
              </div>
            </div>

            {/* 7. Intellectual Property */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                7. Intellectual Property Rights
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  All content, features, and functionality of the Service — including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, software, and the overall design and layout — are owned by the Universal Website Transparency Badge project and are protected by applicable intellectual property laws.
                </p>
                <p className="text-muted-foreground">
                  You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms. This license does not include the right to:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Modify, copy, or distribute any content from the Service without prior written permission",
                    "Use any data mining, robots, or similar data gathering or extraction methods",
                    "Frame or mirror any portion of the Service on any other server or internet-based device",
                    "Use the Service's name, logo, or branding without explicit authorization",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 8. Third-Party Links */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                8. Third-Party Links and Services
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  The Service may contain links to third-party websites or services that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
                </p>
                <p className="text-muted-foreground">
                  Our transparency reports evaluate third-party websites but do not constitute an endorsement, approval, or recommendation of those websites. You acknowledge and agree that we are not responsible for any damage or loss caused by or in connection with your use of or reliance on any third-party content, goods, or services.
                </p>
              </div>
            </div>

            {/* 9. Indemnification */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                9. Indemnification
              </h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  You agree to indemnify, defend, and hold harmless the Universal Website Transparency Badge, its operators, contributors, affiliates, and their respective officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any rights of a third party; or (d) your conduct in connection with the Service.
                </p>
              </div>
            </div>

            {/* 10. Privacy */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                10. Privacy
              </h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  Your use of the Service is also governed by our Privacy Policy, which describes how we handle data. Please review our{" "}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>{" "}
                  to understand our practices. By using the Service, you consent to the data practices described in the Privacy Policy.
                </p>
              </div>
            </div>

            {/* 11. Termination */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                11. Termination
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
                </p>
                <p className="text-muted-foreground">
                  Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including but not limited to ownership provisions, warranty disclaimers, indemnification, and limitations of liability.
                </p>
              </div>
            </div>

            {/* 12. Governing Law */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                12. Governing Law & Dispute Resolution
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions. Any disputes arising from or related to these Terms or the Service shall be resolved through good-faith negotiation first, and if unsuccessful, through appropriate legal channels.
                </p>
                <p className="text-muted-foreground">
                  You agree that any legal action or proceeding relating to your access to or use of the Service shall be instituted in a court of competent jurisdiction.
                </p>
              </div>
            </div>

            {/* 13. Severability */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                13. Severability
              </h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if modification is not possible, shall be severed from these Terms. The remaining provisions shall continue in full force and effect.
                </p>
              </div>
            </div>

            {/* 14. Contact */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">
                14. Contact Information
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Email:</strong>{" "}
                  <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline">
                    hello@transparencybadge.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Related legal documents:
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild variant="outline">
                <Link to="/privacy">Privacy Policy</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/disclaimer">Disclaimer</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/transparency">Transparency & Limitations</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 border-t">
        <div className="container max-w-3xl text-center">
          <p className="text-sm text-muted-foreground">
            Last updated: March 2026
          </p>
        </div>
      </section>
    </Layout>
  );
}